import { Box, Grid, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { DatePicker } from '@mui/x-date-pickers';
import { addMonths, differenceInMinutes, endOfDay, format, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import { List, Map } from 'immutable';
import _ from 'lodash';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import SmallBadge from '@sophocles/components/SmallBadge';
import {
  fetchAnnualSolarReportsForSiteWithRange,
  getAnnualSolarReportsForSelectedStructure,
} from '@sophocles/entities/AnnualSolarReport';
import {
  fetchDailySolarReportsForSiteWithRange,
  getDailySolarReportsForSelectedStructure,
} from '@sophocles/entities/DailySolarReport';
import {
  fetchMonthlySolarReportsForSiteWithRange,
  getMonthlySolarReportsForSelectedStructure,
} from '@sophocles/entities/MonthlySolarReport';
import {
  fetchWeeklySolarReportsForSiteWithRange,
  getWeeklySolarReportsForSelectedStructure,
} from '@sophocles/entities/WeeklySolarReport';
import { getQueryParam } from '@sophocles/utils';
import { getDateFormat } from '@sophocles/utils/formatUtils';
import AnsiDoubleConsumptionChart from '@sophocles/views/sites/components/AnsiDoubleConsumptionChart';
import AnsiDoubleConsumptionTable from '@sophocles/views/sites/components/AnsiDoubleConsumptionTable';
import PowerMenu from '@sophocles/views/sites/components/PowerMenu';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

/**
 * Component Notes:
 * uses date_start and date_end query params for state
 * */
class AnsiDoubleConsumptionPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 'daily',
      loading: false,
    };
  }

  _setDailyTab = () =>
    this.setState({ tab: 'daily' }, () => this._fetchDailyData(this._getStartDate(), this._getEndDate()));

  _setWeeklyTab = () =>
    this.setState({ tab: 'weekly' }, () => this._fetchWeeklyData(this._getStartDate(), this._getEndDate()));

  _setMonthlyTab = () =>
    this.setState({ tab: 'monthly' }, () => this._fetchMonthlyData(this._getStartDate(), this._getEndDate()));

  _setYearlyTab = () =>
    this.setState({ tab: 'yearly' }, () => this._fetchYearlyData(this._getStartDate(), this._getEndDate()));

  _onDateRangeChange = (start, end) => {
    const { router } = this.props;
    const { tab } = this.state;
    const startDate = this._getStartDate();
    const endDate = this._getEndDate();

    if (differenceInMinutes(start, startDate) !== 0 || differenceInMinutes(end, endDate) !== 0) {
      if (tab == 'daily') {
        this._fetchDailyData(start, end);
      } else if (tab == 'weekly') {
        this._fetchWeeklyData(start, end);
      } else if (tab == 'monthly') {
        this._fetchMonthlyData(start, end);
      } else if (tab == 'yearly') {
        this._fetchYearlyData(start, end);
      }

      // create replacement query
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('date_start', format(start, 'yyyy-MM-dd'));
      searchParams.set('date_end', format(end, 'yyyy-MM-dd'));
      const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
      router.navigate(newRelativePathQuery);
    }
  };

  _startLoading = () => this.setState({ loading: true });

  _endLoading = () => this.setState({ loading: false });

  _getStartDate = () => {
    const { router } = this.props;
    const dateStart = getQueryParam(router.location, 'date_start');
    const startDate = dateStart ? parseISO(dateStart) : false;
    const result = startDate || addMonths(new Date(), -2);
    if (!dateStart) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('date_start', format(result, 'yyyy-MM-dd'));
      const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
      this.props.router.navigate(newRelativePathQuery);
    }
    return result;
  };

  _getEndDate = () => {
    const { router } = this.props;
    const dateEnd = getQueryParam(router.location, 'date_end');
    const endDate = dateEnd ? parseISO(dateEnd) : false;
    const result = endDate || new Date();
    if (!dateEnd) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('date_end', format(result, 'yyyy-MM-dd'));
      const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
      this.props.router.navigate(newRelativePathQuery);
    }
    return result;
  };

  _fetchDailyData = (start, end) => {
    const { fetchDailyData, selectedStructure_id } = this.props;

    if (selectedStructure_id != null) {
      this._startLoading();
      fetchDailyData(selectedStructure_id, format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd')).then(() =>
        this._endLoading(),
      );
    }
  };

  _fetchWeeklyData = (start, end) => {
    const { fetchWeeklyData, selectedStructure_id } = this.props;
    if (selectedStructure_id != null) {
      this._startLoading();
      fetchWeeklyData(selectedStructure_id, format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd')).then(() =>
        this._endLoading(),
      );
    }
  };

  _fetchMonthlyData = (start, end) => {
    const { fetchMonthlyData, selectedStructure_id } = this.props;
    if (selectedStructure_id != null) {
      this._startLoading();
      fetchMonthlyData(selectedStructure_id, format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd')).then(() =>
        this._endLoading(),
      );
    }
  };

  _fetchYearlyData = (start, end) => {
    const { fetchYearlyData, selectedStructure_id } = this.props;
    if (selectedStructure_id != null) {
      this._startLoading();
      fetchYearlyData(selectedStructure_id, format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd')).then(() =>
        this._endLoading(),
      );
    }
  };

  componentDidMount() {
    this._fetchDailyData(this._getStartDate(), this._getEndDate());
  }

  _endDateFilter = date => {
    if (typeof date !== 'string') {
      this._onDateRangeChange(this._getStartDate(), date._isAMomentObject ? date.toDate() : date);
    }
  };

  _startDateFilter = date => {
    if (typeof date !== 'string') {
      this._onDateRangeChange(date._isAMomentObject ? date.toDate() : date, this._getEndDate());
    }
  };

  render() {
    const {
      selectedStructure,
      match: { params },
      dailySolarReports,
      weeklySolarReports,
      monthlySolarReports,
      yearlySolarReports,
      user,
      t,
    } = this.props;

    const dateFormat = getDateFormat(user);

    const { tab } = this.state;

    const isStaff = user.get('is_staff', false);

    const unit_id = selectedStructure.getIn(['relay_controller', 'id'], 0);
    const unitTimeZone = selectedStructure.get('time_zone');
    const unit = selectedStructure.get('relay_controller', Map());

    const measuresPower = unit.get('measures_power_consumption_in_watts', false);
    const measuresComplexPower = unit.getIn(['model', 'measures_complex_ac_power'], false);

    const powerOrCurrent = measuresPower ? 'Power' : 'Current';

    // const preFilteredDataList = tab == 0 ? maxPowerDataList : instantPowerDataList;

    const startDate = this._getStartDate();
    const endDate = this._getEndDate();

    const startDateTime = startOfDay(startDate);
    const endDateTime = endOfDay(endDate);

    let dataSource = List();
    let chartTitle = ''; // default to empty chart title

    if (tab == 'daily') {
      chartTitle = t('powerData.dailyAnsiReadings');
      dataSource = dailySolarReports;
    } else if (tab == 'weekly') {
      chartTitle = t('powerData.weeklyAnsiReadings');
      dataSource = weeklySolarReports;
    } else if (tab == 'monthly') {
      chartTitle = t('powerData.monthlyAnsiReadings');
      dataSource = monthlySolarReports;
    } else if (tab == 'yearly') {
      chartTitle = t('powerData.yearlyAnsiReadings');
      dataSource = yearlySolarReports;
    }

    const dataList = dataSource
      .filter(data => {
        const m = data.get('moment');
        return isAfter(m, startDateTime) && isBefore(m, endDateTime);
      })
      .map(data => {
        const generated = data.get('generated', 0);
        const consumed = data.get('consumed', 0);
        return data.merge({
          generated: _.round(generated, 3),
          consumed: _.round(consumed, 3),
          short: format(data.get('moment', new Date()), 'MM/dd'),
        });
      })
      .sortBy(item => item.get('moment'))
      .reverse();

    const plainList = dataList.toJS();
    _.reverse(plainList);

    // const startDateInput = (
    //   <HorizontalDateBox
    //     title="Start Date"
    //     name="start_date"
    //     value={startDate}
    //     labelClassName="no-bottom-margin pad-right"
    //     inputDivClassName="inline"
    //     inputClassName="form-control form-control-sm"
    //     outerClassName="form-group"
    //     onChange={this._startDateFilter}
    //   />
    // );

    // const endDateInput = (
    //   <HorizontalDateBox
    //     title="End Date"
    //     name="end_date"
    //     value={endDate}
    //     labelClassName="no-bottom-margin pad-right"
    //     inputDivClassName="inline"
    //     inputClassName="form-control form-control-sm"
    //     outerClassName="form-group"
    //     rightAlign={true}
    //     onChange={this._endDateFilter}
    //     isValidDate={current => {
    //       if (current._isAMomentObject) {
    //         return isBefore(current.toDate(), new Date());
    //       }
    //       return isBefore(current, new Date());
    //     }} // anything before today
    //   />
    // );

    return (
      <Box sx={{ pb: '24px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Link to={`/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/ticket/new`}>
            {isStaff ? (
              <SmallBadge
                overlap="circular"
                badgeContent={'+'}
                color="primary"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              >
                <IconButton
                  tooltip={t('general.newTicket', 'New Ticket')}
                  tooltipposition="bottom-center"
                  iconstyle={{ color: 'var(--text-secondary)' }}
                >
                  <Icon fontSize="small" className="fas fa-ticket-alt" />
                </IconButton>
              </SmallBadge>
            ) : null}
          </Link>
          <Link to={`/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/tickets`}>
            <IconButton
              tooltip={t('general.supportTickets', 'Support Tickets')}
              tooltipposition="bottom-center"
              iconstyle={{ color: 'var(--text-secondary)' }}
            >
              <Icon fontSize="small" className="fas fa-ticket-alt" />
            </IconButton>
          </Link>
        </Box>
        <Paper sx={{ display: 'flex', flex: 1, mx: 1, flexDirection: 'column', p: 2 }}>
          {/* Filters Section */}
          <Box sx={{ mb: 2, p: 2, border: '1px solid var(--border-strong)', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('general.filters')}
            </Typography>
            <Grid container spacing={2}>
              {/* From Date Picker */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  disabled={this.state.loading}
                  label={t('general.fromDate')}
                  value={startDate}
                  format={dateFormat}
                  onChange={newValue => this._startDateFilter(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              {/* To Date Picker */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  disabled={this.state.loading}
                  label={t('general.toDate')}
                  value={endDate}
                  format={dateFormat}
                  onChange={newValue => this._endDateFilter(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </Box>
          {unit_id != 0 ? (
            <div style={{ width: '100%' }}>
              <Paper
                style={{
                  width: '100%',
                  backgroundColor: 'var(--surface-primary)',
                  padding: '8px 12px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 8 }}>
                    <Button
                      variant="contained"
                      style={{ margin: '0 3px' }}
                      color={tab == 'daily' ? 'primary' : 'secondary'}
                      onClick={() => this._setDailyTab()}
                    >
                      {t('general.daily')}
                    </Button>
                    <Button
                      variant="contained"
                      style={{ margin: '0 3px' }}
                      color={tab == 'weekly' ? 'primary' : 'secondary'}
                      onClick={() => this._setWeeklyTab()}
                    >
                      {t('general.weekly')}
                    </Button>
                    <Button
                      variant="contained"
                      style={{ margin: '0 3px' }}
                      color={tab == 'monthly' ? 'primary' : 'secondary'}
                      onClick={() => this._setMonthlyTab()}
                    >
                      {t('general.monthly')}
                    </Button>
                    <Button
                      variant="contained"
                      style={{ margin: '0 3px' }}
                      label={t('general.yearly', 'Yearly')}
                      color={tab == 'yearly' ? 'primary' : 'secondary'}
                      onClick={() => this._setYearlyTab()}
                    >
                      {t('general.yearly')}
                    </Button>
                    <PowerMenu selectedPage="ansi" site={selectedStructure.toJS()} />
                  </div>
                </div>
                <div>
                  <h4 style={{ textAlign: 'center' }}> {chartTitle} </h4>
                  <AnsiDoubleConsumptionChart list={plainList} />
                  <div style={{ height: '400px' }}>
                    <AnsiDoubleConsumptionTable unit_id={unit_id} {...this.props} list={dataList} />
                  </div>
                </div>
              </Paper>
            </div>
          ) : (
            <h2 style={{ textAlign: 'center' }}>{t('powerData.noReadings')}</h2>
          )}
        </Paper>
      </Box>
    );
  }
}

function mapStateToProps(state) {
  return {
    dailySolarReports: getDailySolarReportsForSelectedStructure(state),
    weeklySolarReports: getWeeklySolarReportsForSelectedStructure(state),
    monthlySolarReports: getMonthlySolarReportsForSelectedStructure(state),
    yearlySolarReports: getAnnualSolarReportsForSelectedStructure(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    // fetchData: (unit_id, from, to) => dispatch(fetchRelayControllerStatusHistoriesForUnitWithRange(unit_id, from, to)),
    fetchDailyData: (site_id, from, to) => dispatch(fetchDailySolarReportsForSiteWithRange(site_id, from, to)),
    fetchWeeklyData: (site_id, from, to) => dispatch(fetchWeeklySolarReportsForSiteWithRange(site_id, from, to)),
    fetchMonthlyData: (site_id, from, to) => dispatch(fetchMonthlySolarReportsForSiteWithRange(site_id, from, to)),
    fetchYearlyData: (site_id, from, to) => dispatch(fetchAnnualSolarReportsForSiteWithRange(site_id, from, to)),
  };
}

const ConnectedAnsiDoubleConsumptionPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(AnsiDoubleConsumptionPage));

function AnsiDoubleConsumptionContainer(props) {
  const { selectedStructure, loading, siteId } = useSiteDashboardLayoutContext();
  const { user } = useUser();
  if (loading) {
    return <LoadingScreen />;
  }
  return (
    <ConnectedAnsiDoubleConsumptionPage
      {...props}
      user={user}
      loading={loading}
      selectedStructure={selectedStructure}
      selectedStructure_id={siteId}
    />
  );
}

export default AnsiDoubleConsumptionContainer;
